/**
 * API Client for Portfolio Co-Pilot
 */

const API_BASE = '/api';

// Storage key for auth token (must match AuthProvider)
const ACCESS_TOKEN_KEY = 'portfolio_access_token';

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

/**
 * Get auth headers if token is available
 */
function getAuthHeaders() {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  }
  return {};
}

/**
 * Authenticated fetch wrapper - use this for all API calls
 * Automatically includes auth headers and handles 401 responses
 */
export async function fetchApi(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  
  // Merge auth headers with any provided headers
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
    ...(options.headers || {}),
  };
  
  const response = await fetch(url, { ...options, headers });
  
  // Handle 401 - redirect to login
  if (response.status === 401) {
    // Clear any stale tokens
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    // Reload to trigger auth flow
    window.location.reload();
    throw new ApiError('Authentication required', 401, {});
  }
  
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new ApiError(
      data.detail || `API Error: ${response.status}`,
      response.status,
      data
    );
  }
  
  return response.json();
}

// Positions
export async function getPositions(account = null, consolidated = false) {
  const params = new URLSearchParams();
  if (account) params.append('account', account);
  if (consolidated) params.append('consolidated', 'true');
  const queryString = params.toString();
  return fetchApi(`/positions${queryString ? '?' + queryString : ''}`);
}

export async function getPositionByTicker(ticker) {
  return fetchApi(`/positions/${ticker}`);
}

// Allocation
export async function getAllocation() {
  return fetchApi('/allocation');
}

// Trades
export async function getTrades(days = 30, account = null, ticker = null) {
  const params = new URLSearchParams({ days });
  if (account) params.append('account', account);
  if (ticker) params.append('ticker', ticker);
  return fetchApi(`/trades?${params}`);
}

export async function logTrade(trade) {
  return fetchApi('/trades', {
    method: 'POST',
    body: JSON.stringify(trade),
  });
}

// Rules
export async function checkRules() {
  return fetchApi('/rules/check');
}

// Import
export async function importCsv(csvContent, format, previewOnly = false, accountId = null) {
  const body = {
    csv_content: csvContent,
    format: format,
    preview_only: previewOnly,
  };
  if (accountId) {
    body.account_id = accountId;
  }
  return fetchApi('/import/csv', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function previewImport(csvContent, format, accountId = null) {
  const body = {
    csv_content: csvContent,
    format: format,
  };
  if (accountId) {
    body.account_id = accountId;
  }
  return fetchApi('/import/preview', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function getImportFormats() {
  return fetchApi('/import/formats');
}

// Snapshots
export async function getSnapshots(limit = 30) {
  return fetchApi(`/snapshots?limit=${limit}`);
}

export async function takeSnapshot() {
  return fetchApi('/snapshots', { method: 'POST' });
}

// Config
export async function getTickersConfig() {
  return fetchApi('/config/tickers');
}

export async function updateTickerConfig(ticker, config) {
  return fetchApi(`/config/tickers/${ticker}`, {
    method: 'PUT',
    body: JSON.stringify(config),
  });
}

export async function getRulesConfig() {
  return fetchApi('/config/rules');
}

export async function getAccountStrategy() {
  return fetchApi('/config/accounts');
}

// Prices
export async function getPrices() {
  return fetchApi('/prices');
}

export async function refreshPrices(tickers = null) {
  const body = tickers ? { tickers } : {};
  return fetchApi('/prices/refresh', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updatePrices(prices) {
  return fetchApi('/prices/update', {
    method: 'POST',
    body: JSON.stringify({ prices }),
  });
}

// Summary
export async function getPortfolioSummary() {
  return fetchApi('/summary');
}

// Health
export async function healthCheck() {
  return fetchApi('/health');
}

// Trade Plans
export async function getTradePlans(status = null, includeItems = false, limit = 10) {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (includeItems) params.append('include_items', 'true');
  if (limit) params.append('limit', limit);
  const queryString = params.toString();
  return fetchApi(`/trade-plans${queryString ? '?' + queryString : ''}`);
}

export async function getActiveTradePlans() {
  return fetchApi('/trade-plans/active');
}

export async function getTradePlan(planId) {
  return fetchApi(`/trade-plans/${planId}`);
}

export async function createTradePlan(plan) {
  return fetchApi('/trade-plans', {
    method: 'POST',
    body: JSON.stringify(plan),
  });
}

export async function updateTradePlanStatus(planId, status, notes = null) {
  return fetchApi(`/trade-plans/${planId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status, notes }),
  });
}

export async function cancelTradePlan(planId, reason) {
  return fetchApi(`/trade-plans/${planId}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function addTradePlanItem(planId, item) {
  return fetchApi(`/trade-plans/${planId}/items`, {
    method: 'POST',
    body: JSON.stringify(item),
  });
}

export async function removeTradePlanItem(planId, itemId) {
  return fetchApi(`/trade-plans/${planId}/items/${itemId}`, {
    method: 'DELETE',
  });
}

export async function executeTradePlanItem(itemId, executedShares, executedPrice, notes = null) {
  return fetchApi(`/trade-plans/items/${itemId}/execute`, {
    method: 'POST',
    body: JSON.stringify({
      executed_shares: executedShares,
      executed_price: executedPrice,
      notes,
    }),
  });
}

export async function skipTradePlanItem(itemId, reason) {
  return fetchApi(`/trade-plans/items/${itemId}/skip?reason=${encodeURIComponent(reason)}`, {
    method: 'POST',
  });
}

export async function reconcileTradePlan(planId, days = 30) {
  return fetchApi(`/trade-plans/${planId}/reconcile?days=${days}`);
}

// Dashboard v2 endpoints
export async function getClusters() {
  return fetchApi('/clusters');
}

export async function getScorecardsForDashboard(status = null) {
  const params = status ? `?status=${status}` : '';
  return fetchApi(`/scorecards${params}`);
}

export async function getConfigHealth() {
  return fetchApi('/config/health');
}

export async function getUpcomingEarnings(days = 30) {
  return fetchApi(`/earnings/upcoming?days=${days}`);
}

// Deployment Plans
export async function getDeploymentPlans(params = {}) {
  const query = new URLSearchParams();
  if (params.status) query.set('status', params.status);
  if (params.direction) query.set('direction', params.direction);
  if (params.ticker) query.set('ticker', params.ticker);
  if (params.include_tranches !== undefined) query.set('include_tranches', params.include_tranches);
  const qs = query.toString();
  return fetchApi(`/deployment-plans${qs ? '?' + qs : ''}`);
}

export async function getDeploymentPlan(planId) {
  return fetchApi(`/deployment-plans/${planId}`);
}

export async function getEligibleTranches() {
  return fetchApi('/deployment-plans/eligible');
}

export async function createDeploymentPlan(data) {
  return fetchApi('/deployment-plans', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDeploymentPlan(planId, data) {
  return fetchApi(`/deployment-plans/${planId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function pauseDeploymentPlan(planId, notes = null) {
  return fetchApi(`/deployment-plans/${planId}/pause`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  });
}

export async function resumeDeploymentPlan(planId) {
  return fetchApi(`/deployment-plans/${planId}/resume`, { method: 'POST' });
}

export async function cancelDeploymentPlan(planId, notes = null) {
  return fetchApi(`/deployment-plans/${planId}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  });
}

export async function completeDeploymentPlan(planId, notes = null) {
  return fetchApi(`/deployment-plans/${planId}/complete`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  });
}

export async function addDeploymentTranche(planId, data) {
  return fetchApi(`/deployment-plans/${planId}/tranches`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function promoteTranche(trancheId) {
  return fetchApi(`/deployment-tranches/${trancheId}/promote`, { method: 'POST' });
}

export async function skipTranche(trancheId, notes = null) {
  return fetchApi(`/deployment-tranches/${trancheId}/skip`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  });
}

export async function unskipTranche(trancheId) {
  return fetchApi(`/deployment-tranches/${trancheId}/unskip`, { method: 'POST' });
}

export async function updateTranche(trancheId, data) {
  return fetchApi(`/deployment-tranches/${trancheId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function migrateFromPipeline() {
  return fetchApi('/deployment-plans/migrate', { method: 'POST' });
}

// Oscillator (S&P Breadth)
export async function getOscillatorSignal() {
  return fetchApi('/oscillator/signal');
}

export async function getOscillatorCurrent() {
  return fetchApi('/oscillator/current');
}

export async function getOscillatorHistory(days = 30) {
  const params = new URLSearchParams({ days: String(days) });
  return fetchApi(`/oscillator/history?${params}`);
}

export async function getOscillatorExplain(date = null) {
  const qs = date ? `?date=${encodeURIComponent(date)}` : '';
  return fetchApi(`/oscillator/explain${qs}`);
}

// Market indices (Dow / S&P 500 / Nasdaq 100 / VIX)
export async function getIndices(tickers = null) {
  const qs = tickers && tickers.length
    ? `?${tickers.map((t) => `tickers=${encodeURIComponent(t)}`).join('&')}`
    : '';
  return fetchApi(`/market/indices${qs}`);
}

export const api = {
  getPositions,
  getPositionByTicker,
  getAllocation,
  getTrades,
  logTrade,
  checkRules,
  importCsv,
  previewImport,
  getImportFormats,
  getSnapshots,
  takeSnapshot,
  getTickersConfig,
  updateTickerConfig,
  getRulesConfig,
  getAccountStrategy,
  getPrices,
  refreshPrices,
  updatePrices,
  getPortfolioSummary,
  healthCheck,
  // Trade Plans
  getTradePlans,
  getActiveTradePlans,
  getTradePlan,
  createTradePlan,
  updateTradePlanStatus,
  cancelTradePlan,
  addTradePlanItem,
  removeTradePlanItem,
  executeTradePlanItem,
  skipTradePlanItem,
  reconcileTradePlan,
  // Dashboard v2
  getClusters,
  getScorecardsForDashboard,
  getConfigHealth,
  getUpcomingEarnings,
  // Deployment Plans
  getDeploymentPlans,
  getDeploymentPlan,
  getEligibleTranches,
  createDeploymentPlan,
  updateDeploymentPlan,
  pauseDeploymentPlan,
  resumeDeploymentPlan,
  cancelDeploymentPlan,
  completeDeploymentPlan,
  addDeploymentTranche,
  promoteTranche,
  skipTranche,
  unskipTranche,
  updateTranche,
  migrateFromPipeline,
  // Oscillator
  getOscillatorSignal,
  getOscillatorCurrent,
  getOscillatorHistory,
  getOscillatorExplain,
  // Market indices
  getIndices,
};

export default api;

